import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, MaskedLines, Eyebrow, Marquee, Counter } from "@/components/Motion";
import { img, REGIONS } from "@/lib/content";

const VALUES = [
  { name: "Precision", text: "We would rather present three exceptional candidates than thirty adequate ones." },
  { name: "Discretion", text: "Confidentiality is the currency of executive search. We spend it carefully." },
  { name: "Partnership", text: "We measure success in retained hires and long relationships — not placements." },
  { name: "Accountability", text: "Every mandate has a named senior consultant and a committed outcome." },
];

const About = () => (
  <>
    <Seo
      title="About — NEXREQ UAE & GCC"
      siteName="NEXREQ"
      description="NEXREQ is a North American recruitment organization serving the UAE and GCC with executive search, direct hire, RPO and talent advisory."
    />
    <PageHero
      eyebrow="About NEXREQ"
      lines={["North American Standards.", "GCC Market Expertise."]}
      description="NEXREQ is a North American recruitment organization with operations and delivery capabilities serving the United States, Canada, the UAE and GCC markets."
      image={img("photo-1518684079-3c830dcef090", 1920)}
    />

    <section className="bg-obsidian py-24 lg:py-32" data-testid="about-story">
      <div className="nx-container grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Our Story</Eyebrow>
          </Reveal>
          <h2 className="mt-6 font-heading text-3xl font-light leading-[1.08] tracking-tight text-white sm:text-4xl">
            <MaskedLines lines={["The Bridge Between", "Two Talent Worlds."]} />
          </h2>
        </div>
        <div className="space-y-6 leading-relaxed text-white/55 lg:col-span-7">
          <Reveal delay={0.1}>
            <p>
              NEXREQ was built on a simple observation: the Gulf's most ambitious organizations
              deserve the same rigour, discretion and accountability that define the best of North
              American executive search — delivered by people who genuinely understand the region.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p>
              From our Dubai office and North American delivery network, we connect exceptional
              talent with exceptional organizations across the UAE, Saudi Arabia, Qatar, Oman,
              Bahrain and Kuwait. Our consultants combine international search methodology with the
              relationships, cultural fluency and market intelligence that GCC hiring demands.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <p className="font-heading text-xl font-light italic text-white/75">
              "Your People Partner in Every Hire" is not a tagline. It is the standard every NEXREQ
              engagement is measured against.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <Marquee
      className="border-y border-white/[0.08] bg-surface py-6 font-heading text-xl font-light uppercase tracking-[0.2em] text-white/20 lg:text-2xl"
      items={["Global Talent", "Regional Expertise", "Precision Hiring Across the GCC", "Your People Partner in Every Hire"]}
    />

    <section className="bg-obsidian py-24 lg:py-32" data-testid="about-values">
      <div className="nx-container">
        <Reveal>
          <Eyebrow>What We Stand For</Eyebrow>
        </Reveal>
        <div className="mt-14 grid gap-px border border-white/[0.08] bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.name} delay={i * 0.07}>
              <div className="h-full bg-obsidian p-9" data-testid={`about-value-${i}`}>
                <span className="font-mono text-[10px] text-emerald-500/80">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 font-heading text-xl font-light text-white">{v.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/50">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-surface py-24 text-white lg:py-32" data-testid="about-presence">
      <div className="nx-container grid gap-14 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <Eyebrow>Presence</Eyebrow>
          </Reveal>
          <h2 className="mt-6 font-heading text-3xl font-light leading-[1.08] tracking-tight sm:text-4xl">
            <MaskedLines lines={["Dubai Head Office,", "International Delivery."]} />
          </h2>
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-6 text-[15px] leading-relaxed text-white/55">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Dubai Office</p>
                <p className="mt-2">
                  Bin Dasmal Building – 1<br />
                  Al Quoz Industrial First<br />
                  Dubai, United Arab Emirates
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Contact</p>
                <p className="mt-2">
                  <a href="mailto:info@nexreq.ae" className="text-emerald-400 hover:underline" data-testid="about-email-link">info@nexreq.ae</a>
                  <br />
                  <a href="https://www.nexreq.ae" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline" data-testid="about-website-link">www.nexreq.ae</a>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.25} className="lg:col-span-6">
          <div className="overflow-hidden">
            <img
              src={img("photo-1522071820081-009f0129c71c", 1400)}
              alt="NEXREQ team collaborating"
              className="aspect-[16/10] w-full object-cover grayscale-[25%]"
              loading="lazy"
            />
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {REGIONS.map((r) => (
              <span key={r} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                <span className="h-1 w-1 rounded-full bg-emerald-600" />
                {r}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-obsidian py-24" data-testid="about-cta">
      <div className="nx-container grid gap-10 lg:grid-cols-[2fr_1fr] lg:items-end">
        <h2 className="font-heading text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl">
          <MaskedLines lines={["Let's Build What's Next."]} />
        </h2>
        <Reveal delay={0.2} className="lg:justify-self-end">
          <Link to="/contact" className="nx-btn-primary" data-testid="about-contact-btn">
            Contact NEXREQ <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
      <div className="nx-container mt-16 grid grid-cols-2 gap-10 border-t border-white/10 pt-12 lg:grid-cols-4">
        {[
          { to: 4, label: "Operating Regions" },
          { to: 9, label: "Markets Served" },
          { to: 8, label: "Core Sectors" },
          { to: 4, label: "Search Disciplines" },
        ].map((s) => (
          <div key={s.label}>
            <div className="font-heading text-4xl font-light text-white lg:text-5xl">
              <Counter to={s.to} />
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  </>
);

export default About;
